import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { getDynamicContent } from "../../../utils/helpers/category-helper";
import { trackDesktopCustomEventsAU } from "../../../tracking";
import { AU_DESKTOP_EVENTS } from "../../../tracking/au.desktop-events";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import styles from "./styles.css";

const propTypes = {
    dynamicContent: PropTypes.object,
    categories: PropTypes.array,
    selectedCategory: PropTypes.object,
    showHeader: PropTypes.bool,
    crmVariant: PropTypes.bool
};

const TableData = React.memo(({ data }) => (
    <ul styleName="styles.tableData">
        {data.map(({ numberOfCars = "", priceRange = "", seoLink = "", seoText = "" }) => (
            <li key={seoText}>
                <a href={seoLink}>{seoText}</a>
                <span>{numberOfCars}</span>
                <span>{priceRange}</span>
            </li>
        ))}
    </ul>
));

TableData.propTypes = {
    data: PropTypes.array.isRequired
};

const Description = React.memo(({ body, handleReadMore }) => (
    <React.Fragment>
        {body.map((data, index) => (
            <p
            // eslint-disable-next-line no-magic-numbers
                key={`${data.slice(0, 20)}-${index}`}
                styleName="styles.usedCarDescription"
                dangerouslySetInnerHTML={{ __html: data }}
            />
        ))}<span
            onClick={handleReadMore}
            styleName="styles.linkColor"
        >
            Read Less
        </span>
    </React.Fragment>
));

Description.propTypes = {
    body: PropTypes.array.isRequired,
    handleReadMore: PropTypes.func.isRequired
};

const SpecificCarDescription = ({
    dynamicContent = {},
    categories = [],
    selectedCategory = "",
    showHeader = true,
    crmVariant = false
}) => {
    const ref = useRef(null);
    const [showReadMore, setShowReadMore] = useState(false);
    const [showLink, setShowLink] = useState(false);

    const { h1, h1Body, tableData = [] } = useMemo(() => ({
        ...getDynamicContent({ dynamicContent, categories, selectedCategory }),
        tableData: dynamicContent?.tableData || []
    }), [dynamicContent, categories, selectedCategory]);

    useEffect(() => {
        if (ref.current && ref.current.clientWidth < ref.current.scrollWidth) {
            setShowLink(true);
        }
    }, []);

    const handleReadMore = async () => {
        setShowReadMore(prev => !prev);
        await yieldToMain();
        trackDesktopCustomEventsAU(
            !showReadMore
                ? AU_DESKTOP_EVENTS.LISTING_CONTENT_READ_MORE
                : AU_DESKTOP_EVENTS.LISTING_CONTENT_READ_LESS
        );
    };

    if (crmVariant) {
        return null;
    }

    if (!h1 || !showHeader) {
        return (
            <div styleName="styles.outer">
                <div className="container" styleName="styles.wrapper">
                    <p styleName="styles.usedCarDescription">
                        {dynamicContent.metaDescription}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div styleName="styles.outer">
            <div className="container" styleName="styles.wrapper">
                <h1 styleName="styles.header">{h1}</h1>

                {showReadMore ? (
                    <React.Fragment><Description body={h1Body} handleReadMore={handleReadMore} />{Array.isArray(tableData) && <TableData data={tableData} />}</React.Fragment>
                ) : (
                    h1Body && (
                        <p>
                            <span ref={ref} styleName="styles.tableContainer">
                                {h1Body[0]}
                            </span>
                            {showLink && (
                                <span
                                    styleName="styles.linkColor"
                                    className={showReadMore ? "opacity-70" : ""}
                                    onClick={handleReadMore}
                                >
                                    Read More
                                </span>
                            )}
                            {Array.isArray(tableData) && (
                                <div styleName="styles.visibilityHidden">
                                    <TableData data={tableData} />
                                </div>
                            )}
                        </p>
                    )
                )}
            </div>
        </div>
    );
};

SpecificCarDescription.propTypes = propTypes;

export default React.memo(SpecificCarDescription);
